import { CheckCircle2, AlertCircle, Info } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Plain wording for each reason the homepage could not be checked
 */
const COULD_NOT_CHECK_TEXT = {
  blocked_by_site: "the site blocks automated checks",
  page_may_be_incomplete: "the fetched page may be incomplete",
  page_too_large: "the page is too large or complex to check",
  fetch_failed: "the page could not be reached",
  dns_failed: "the page could not be reached",
  unsupported_encoding: "the page could not be read",
  check_limit_reached: "today's limit of homepage checks has been reached",
  check_unavailable: "the check is not available at the moment",
  check_failed: "the check is not available at the moment",
};
const REFUSED_ADDRESS = "the address was refused";

const OUTCOME = {
  found: {
    label: "Met",
    icon: CheckCircle2,
    iconClass: "text-success-500",
    badgeClass: "bg-success-50 text-success-700",
  },
  not_found: {
    label: "Not met",
    icon: AlertCircle,
    iconClass: "text-warning-500",
    badgeClass: "bg-warning-50 text-warning-700",
  },
  could_not_check: {
    label: "Not assessed",
    icon: Info,
    iconClass: "text-[var(--accent)]",
    badgeClass: "bg-[rgba(31,76,84,0.06)] text-[var(--text-soft)]",
  },
};

const isWebAddress = (value) => typeof value === "string" && /^https?:\/\//i.test(value);

function describe(homepage) {
  if (homepage.status === "found") {
    return homepage.reason === "page_data_statement_url"
      ? "A statement link was found in the homepage's page data."
      : "The homepage links to the statement.";
  }
  if (homepage.status === "not_found") {
    return "No statement link was detected in the fetched homepage.";
  }
  return `Could not check: ${COULD_NOT_CHECK_TEXT[homepage.reason] ?? REFUSED_ADDRESS}.`;
}

/**
 * Result of the homepage check (Q1) for this request, shown when a homepage was checked.
 * Only results from v1.11 on carry homepage_checked_at. Older results are left as they
 * were: their "not_found" came from a check that could not tell an incomplete page from
 * a page without a link, and "unknown" results keep their note on the TISC card.
 */
export default function HomepageCheckNote({ externalChecks }) {
  const homepage = externalChecks?.homepage;
  if (
    !externalChecks?.homepage_checked_at ||
    !homepage ||
    !OUTCOME[homepage.status] ||
    homepage.reason === "no_homepage_url"
  ) {
    return null;
  }

  const outcome = OUTCOME[homepage.status];
  const Icon = outcome.icon;
  const matchedUrl = isWebAddress(homepage.matched_url) ? homepage.matched_url : null;

  return (
    <section className="flex items-start gap-3 rounded-2xl border border-[rgba(31,76,84,0.12)] bg-white/90 px-5 py-4">
      <Icon className={cn("mt-0.5 h-4 w-4 shrink-0", outcome.iconClass)} aria-hidden="true" />
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-medium text-[var(--text-primary)]">Homepage link to the statement (Q1)</p>
          <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider", outcome.badgeClass)}>
            {outcome.label}
          </span>
        </div>
        <p className="text-sm text-[var(--text-secondary)]">{describe(homepage)}</p>
        {matchedUrl && (
          <a
            href={matchedUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="block truncate text-xs text-[var(--accent)] underline-offset-2 hover:underline"
          >
            {matchedUrl}
          </a>
        )}
        <p className="text-xs text-[var(--text-soft)]">Automated check; prominence not assessed.</p>
      </div>
    </section>
  );
}
